import React, { useEffect, useState, useRef, forwardRef } from "react";
import { AbsDropDown, Button, Input, Label, TextArea } from "abs-ui-components";
import { MDBRow, MDBCol } from "mdbreact";
import { useFormContext, Controller, useWatch } from "react-hook-form";
import "./FeuB1ShipDetails.scss";
import { preventScientificNotation } from "../../../../../services/utilities";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { useParams } from "react-router";
import { Autocomplete, TextField } from "@mui/material";
import { RadioGroup, FormControlLabel, Radio } from "@mui/material";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";
import { createB1Payload, REQUIRED_FIELDS_FOR_B1, AUTOSAVE_SUCCESS_MESSAGE } from "../../FeuConstants";
import AutoSave from "../../../../../common/AutoSave/AutoSave";

const B1ShipDetails = forwardRef(({ B1ShipDetailsData, onFieldAutoSave, B1ShipDropdownValues, refetchMonitoringData, monitoringData, vesselMstId }, ref) => {
	const params = useParams();
	const {
		control,
		handleSubmit,
		formState: { errors, isDirty },
		trigger,
		watch,
		getValues,
		setValue,
		reset,
	} = useFormContext();

	const [isEfficiencyValueRequired, setIsEfficiencyValueRequired] = useState();
	const [dropdownOptions, setDropdownOptions] = useState({
		countryOwnerOptions: [],
		iceClassOptions: [],
		countryFlagOptions: [],
		technicalEfficiencyOptions: [],
		classificationSocietyOptions: [],
		shipTypes: [],
	});
	const [mrvVesselTypeMstId, setMrvVesselTypeMstId] = useState(null);
	const selectedShipType = useWatch({ control, name: "shipType" });
	const [subTypeOptions, setSubTypeOptions] = useState([]);
	const [isSubTypeRequired, setIsSubTypeRequired] = useState();
	const techEffId = B1ShipDetailsData?.VesselTechnicalEfficiencyDtls?.[0]?.TechnicalEfficiencyMstId;
	const matchedOption = dropdownOptions?.technicalEfficiencyOptions?.find((opt) => opt.technicalEfficiencyMstId === techEffId);
	const manualSaveTriggered = useRef(false);

	useEffect(() => {
		if (!B1ShipDetailsData && !monitoringData) return;
		const pick = (b1Val, monitorVal, fallback = "") =>
			b1Val !== null && b1Val !== undefined ? b1Val : monitorVal !== null && monitorVal !== undefined ? monitorVal : fallback;
		const pickNumberOrNull = (b1Val, monitorVal) =>
			b1Val !== null && b1Val !== undefined ? b1Val : monitorVal !== null && monitorVal !== undefined ? monitorVal : null;

		const mrvSelectedShip = B1ShipDetailsData?.MrvVesselTypeMst?.MrvVesselTypeMstId;
		const selecteshiptype = dropdownOptions?.shipTypes?.find((item) => item.mrvVesselTypeMstId === mrvSelectedShip);
		setValue("shipName", pick(B1ShipDetailsData?.shipName ?? B1ShipDetailsData?.VesselName, monitoringData?.shipName, ""));
		setValue(
			"imoNumber",
			pick(
				B1ShipDetailsData?.ImoNumber ?? B1ShipDetailsData?.imoNumber,
				monitoringData?.imoNumber ?? monitoringData?.b1ShipIdentification?.imoNumber ?? ""
			)
		);
		setValue("portOfRegistry", pick(B1ShipDetailsData?.PortCodeMst?.PortName ?? B1ShipDetailsData?.portOfRegistry, monitoringData?.portOfRegistry ?? ""));
		setValue("homePort", pick(B1ShipDetailsData?.homePort, monitoringData?.homePort ?? ""));
		setValue("shipOwnerName", pick(B1ShipDetailsData?.shipOwnerName, monitoringData?.shipOwnerName ?? ""));
		setValue("shipOwnerTaxNumber", pick(B1ShipDetailsData?.shipOwnerTaxNumber, monitoringData?.shipOwnerTaxNumber ?? ""));
		setValue("shipOwnerContactPerson", pick(B1ShipDetailsData?.shipOwnerContactPerson, monitoringData?.shipOwnerContactPerson ?? ""));
		setValue("businessAddress", pick(B1ShipDetailsData?.businessAddress, monitoringData?.businessAddress ?? ""));
		setValue("city", pick(B1ShipDetailsData?.city, monitoringData?.city ?? ""));
		setValue("stateProvinceRegion", pick(B1ShipDetailsData?.stateProvinceRegion, monitoringData?.stateProvinceRegion ?? ""));
		setValue("businessTelephone", pick(B1ShipDetailsData?.businessTelephone, monitoringData?.businessTelephone ?? ""));
		setValue("businessEmail", pick(B1ShipDetailsData?.businessEmail, monitoringData?.businessEmail ?? ""));
		setValue("registeredOwnerIMO", pick(B1ShipDetailsData?.registeredOwnerIMO, monitoringData?.registeredOwnerIMO ?? ""));
		setValue("deadweight", pickNumberOrNull(B1ShipDetailsData?.DeadWeight ?? B1ShipDetailsData?.deadweight, monitoringData?.deadweight));
		setValue(
			"grossTonnage",
			pickNumberOrNull(Math.round(B1ShipDetailsData?.GrossTonnage ?? B1ShipDetailsData?.grossTonnage), Math.round(monitoringData?.grossTonnage))
		);
		setValue("registeredOwnerCountry", pick(B1ShipDetailsData?.registeredOwnerCountry, monitoringData?.registeredOwnerCountry ?? ""));
		setValue("classificationSociety", pick(B1ShipDetailsData?.classificationSociety, monitoringData?.classificationSociety ?? ""));
		setValue("flag", pick(B1ShipDetailsData?.PortCodeMst?.CountryName ?? B1ShipDetailsData?.flag, monitoringData?.flag ?? ""));
		const apiShipType = pick(B1ShipDetailsData?.shipType ?? B1ShipDetailsData?.shipType, monitoringData?.shipType ?? selecteshiptype?.value ?? "");
		const apiShipSubType = pick(B1ShipDetailsData?.shipSubType, monitoringData?.shipSubType ?? "");

		// Find the matching option by LABEL (not value)
		const matchedType = dropdownOptions.shipTypes?.find((opt) => opt.label === apiShipType);

		if (matchedType) {
			setValue("shipType", matchedType.value); // Set the VALUE (code) that matches dropdown options
		} else {
			setValue("shipType", "");
		}

		const subTypeOptions = dropdownOptions?.shipTypes ?? [];
		const matchedSub = subTypeOptions.find((o) => String(o.code).trim().toLowerCase() === String(apiShipSubType).trim().toLowerCase());
		if (matchedSub) {
			setValue("shipSubType", matchedSub.value);
		} else {
			setValue("shipSubType", apiShipSubType ?? "");
		}

		setValue("technicalEfficiency", matchedOption?.code ?? pick(B1ShipDetailsData?.technicalEfficiency, monitoringData?.technicalEfficiency ?? ""));
		const teValue =
			B1ShipDetailsData && B1ShipDetailsData.hasOwnProperty("technicalEfficiencyValue")
				? B1ShipDetailsData.technicalEfficiencyValue
				: monitoringData && monitoringData.hasOwnProperty("technicalEfficiencyValue")
				? monitoringData.technicalEfficiencyValue
				: null;
		setValue("technicalEfficiencyValue", teValue === 0 ? 0 : teValue ?? null);
		const hasIceClass = pick(
			B1ShipDetailsData?.iceClass,
			B1ShipDetailsData?.IceClassMst?.IceClassMstId ?? monitoringData?.iceClass ?? monitoringData?.IceClassMst?.IceClassMstId
		);
		setValue("isIceClassShip", Boolean(hasIceClass));
		setValue("iceClass", pick(B1ShipDetailsData?.IceClassMst?.Code ?? B1ShipDetailsData?.iceClass, monitoringData?.iceClass ?? ""));
		setValue("additionalInformation", pick(B1ShipDetailsData?.additionalInformation, monitoringData?.additionalInformation ?? ""));
		setValue("businessPrinciplePlace", pick(B1ShipDetailsData?.businessPrinciplePlace, monitoringData?.businessPrinciplePlace ?? ""));
	}, [B1ShipDetailsData, monitoringData, setValue, dropdownOptions, matchedOption]);

	useEffect(() => {
		if (!B1ShipDetailsData || !dropdownOptions?.shipTypes?.length) return;

		const selectedShipTypeObj = dropdownOptions.shipTypes.find(
			(type) =>
				type.mrvVesselTypeMstId === B1ShipDetailsData?.MrvVesselTypeMst?.MrvVesselTypeMstId ||
				type.value?.trim()?.toLowerCase() === B1ShipDetailsData.shipType?.trim()?.toLowerCase()
		);

		if (selectedShipTypeObj) {
			const shipTypeLabel = selectedShipTypeObj.label;
			const isSubTypeReq = ["Passenger ship", "Other ship types"].includes(shipTypeLabel);

			setValue("shipType", selectedShipTypeObj.value, { shouldValidate: true });
			setMrvVesselTypeMstId(selectedShipTypeObj.mrvVesselTypeMstId);
			setIsSubTypeRequired(isSubTypeReq);
		}
	}, [B1ShipDetailsData, dropdownOptions?.shipTypes, setValue]);

	useEffect(() => {
		if (B1ShipDropdownValues?.shipTypes?.length && mrvVesselTypeMstId) {
			const subtypes = B1ShipDropdownValues.shipTypes.filter((type) => type.parentMrvVesselTypeId === mrvVesselTypeMstId);

			if (subtypes?.length > 0) {
				const mappedSubtypes = subtypes.map((sub) => ({
					label: sub.value,
					value: sub.code,
				}));
				setSubTypeOptions(mappedSubtypes);
			} else {
				setSubTypeOptions([]);
			}
		}
	}, [mrvVesselTypeMstId, B1ShipDropdownValues]);

	useEffect(() => {
		if (!subTypeOptions?.length) return;

		const apiSubTypeCode = B1ShipDetailsData?.shipSubType ?? monitoringData?.shipSubType ?? "";

		if (!apiSubTypeCode) {
			setValue("shipSubType", "", { shouldValidate: false });
			return;
		}

		const matchedSub = subTypeOptions.find((o) => String(o.value).trim().toLowerCase() === String(apiSubTypeCode).trim().toLowerCase());

		if (matchedSub) {
			setValue("shipSubType", matchedSub.value, { shouldValidate: true });
		} else {
			setValue("shipSubType", apiSubTypeCode, { shouldValidate: false });
		}
	}, [subTypeOptions, B1ShipDetailsData?.shipSubType, monitoringData?.shipSubType, setValue]);

	const selectedTechnicalEfficiency = watch("technicalEfficiency");
	const isTextDisabled = selectedTechnicalEfficiency?.label === "Not Applicable";

	useEffect(() => {
		setIsEfficiencyValueRequired(
			B1ShipDetailsData &&
				B1ShipDetailsData?.technicalEfficiency !== "" &&
				!["na", "not applicable"].includes(B1ShipDetailsData?.technicalEfficiency?.toLowerCase())
		);
	}, [B1ShipDetailsData]);

	const techEffMd = isEfficiencyValueRequired ? "2" : "4";
	const watchedIsIceClassShip = watch("isIceClassShip");
	const isIceClassYes = ["true", true].includes(watchedIsIceClassShip);
	const iceClassMd = isIceClassYes ? "2" : "4";

	useEffect(() => {
		if (!B1ShipDropdownValues) return;
		const dropdownMap = [
			{ apiKey: "countryCodes", stateKey: "countryOwnerOptions" },
			{ apiKey: "countryCodes", stateKey: "countryFlagOptions" },
			{ apiKey: "iceClassPolarCodes", stateKey: "iceClassOptions" },
			{ apiKey: "technicalEfficiencyCodes", stateKey: "technicalEfficiencyOptions" },
			{ apiKey: "classificationSocieties", stateKey: "classificationSocietyOptions" },
			{ apiKey: "shipTypes", stateKey: "shipTypes" },
		];

		const formattedOptions = {};
		dropdownMap.forEach(({ apiKey, stateKey }) => {
			const list = B1ShipDropdownValues?.[apiKey] ?? [];

			formattedOptions[stateKey] = list
				.map((item) => {
					switch (stateKey) {
						case "countryOwnerOptions":
							return {
								label: item.countryName,
								value: item.countryCode,
							};
						case "countryFlagOptions":
							return {
								label: item.countryName,
								value: item.countryCode,
							};
						case "technicalEfficiencyOptions":
							return {
								label: item.value,
								value: item.code,
								technicalEfficiencyMstId: item.technicalEfficiencyMstId,
							};
						case "shipTypes":
							return {
								label: item.value,
								value: item.code,
								mrvVesselTypeMstId: item.mrvVesselTypeMstId,
								parentMrvVesselTypeId: item.parentMrvVesselTypeId,
							};
						case "iceClassOptions":
							return {
								label: item.value,
								value: item.code,
							};
						default:
							return {
								label: item.name,
								value: item.code,
							};
					}
				})
				.filter((option) => option.label && option.value);
		});

		setDropdownOptions((prev) => ({ ...prev, ...formattedOptions }));
	}, [B1ShipDropdownValues]);

	const safeGetOptionLabel = (option) => {
		if (typeof option === "string") return option;
		if (option?.inputValue) return option.inputValue;
		return option?.label ?? "";
	};

	useEffect(() => {
		if (isTextDisabled) {
			setValue("technicalEfficiencyText", "");
		}
	}, [isTextDisabled, setValue]);

	const handleBlur = async (field) => {
		const isValid = await trigger(field);
		if (!isValid) return;
		const value = getValues(field);
		onFieldAutoSave(field, value);
	};

	const handleImoNumberBlur = async (field, value) => {
		const isValid = await trigger(field);
		if (isValid && value?.length === 7) {
		}
	};

	//Autosave call
	AutoSave(ref, REQUIRED_FIELDS_FOR_B1, async () => {
		if (!isDirty) return;
		const formValues = getValues();
		await onSubmit(formValues, true);
	});

	const onSubmit = async (data, isAuto = false) => {
		const payload = createB1Payload(data);
		try {
			const response = await callApi({
				method: "POST",
				url: API_ROUTES.B1(vesselMstId),
				data: payload,
			});

			if (response?.isSuccess) {
				emitToasterMsg("success", "Table B.1", isAuto ? AUTOSAVE_SUCCESS_MESSAGE : "Created successfully");

				const currentFormValues = getValues();

				if (typeof refetchMonitoringData === "function") {
					await refetchMonitoringData();
				}
				reset(getValues());
			} else {
				emitToasterMsg("error", "Table B.1", response?.errorMessage || "Save failed");
			}
		} catch (error) {
			console.error("API Error:", error);
			emitToasterMsg("error", "Table B.1", error.errorMessage);
		}
	};

	return (
		<>
			<div className="form-section mt-5">
				<MDBRow className="align-items-center mb-2">
					<MDBCol md="4">
						<Label name="Name of the ship" bold required />
						<Controller
							name="shipName"
							control={control}
							rules={{
								required: "Field is required",
								validate: (value) => value?.length <= 255 || "Please enter a value with size between 0 and 255.",
							}}
							defaultValue={B1ShipDetailsData?.shipName ?? ""}
							render={({ field }) => (
								<>
									<Input
										id="name_of_the_ship"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("shipName")}
										maxLength={255}
										className="form-input"
										required
									/>
									<div className="error-msg-b1ship">{errors?.shipName?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
					{/* IMO Number */}
					<MDBCol md="4">
						<Label name="IMO ship identification number" bold required />
						<Controller
							name="imoNumber"
							control={control}
							rules={{
								required: "Please provide a 7 digit IMO number",
								validate: (value) => value?.length === 7 || "IMO number must be exactly 7 characters",
							}}
							defaultValue={B1ShipDetailsData?.imoNumber ?? ""}
							render={({ field }) => (
								<>
									<Input
										id="vessel_imo_number"
										type="number"
										autoFocus={false}
										value={field?.value}
										onChange={(e) => field.onChange(e)}
										className="input-height"
										required
										min="1"
										minLength={7}
										maxLength={7}
										onBlur={(e) => handleImoNumberBlur(field.name, field.value)}
										onWheel={(e) => e.target.blur()}
										onKeyDown={preventScientificNotation}
										disabled
										onInput={(e) => {
											if (e.target.value?.length > 7) {
												e.target.value = e.target.value.slice(0, 7);
											}
										}}
									/>
									<div className="error-msg-b1ship">{errors?.imoNumber?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>

					{/* Port of Registry */}
					<MDBCol md="4">
						<Label name="Port of registry" bold />
						<Controller
							name="portOfRegistry"
							control={control}
							defaultValue={B1ShipDetailsData?.portOfRegistry ?? ""}
							render={({ field }) => (
								<>
									<Input
										id="port_of_registry"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("portOfRegistry")}
										maxLength={255}
										className="form-input"
										required
									/>
									<div className="error-msg-b1ship">{errors?.portOfRegistry?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
				</MDBRow>

				<MDBRow className="align-items-center mb-2">
					<MDBCol md="4">
						<Label name="Home port (if not identical with port of registry)" bold />
						<Controller
							name="homePort"
							defaultValue={B1ShipDetailsData?.homePort ?? ""}
							render={({ field }) => (
								<>
									<Input
										id="home_port"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("homePort")}
										maxLength={255}
										className="form-input"
									/>
									<div className="error-msg-b1ship">{errors?.HomePort?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
					<MDBCol md="4">
						<Label name="Name of the shipowner" bold required />
						<Controller
							name="shipOwnerName"
							control={control}
							rules={{
								required: "Field is required",
							}}
							defaultValue={B1ShipDetailsData?.shipOwnerName ?? ""}
							render={({ field }) => (
								<>
									<Input
										id="ship_owner_name"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("shipOwnerName")}
										className="form-input"
										required
									/>
									<div className="error-msg-b1ship">{errors?.shipOwnerName?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
					<MDBCol md="4">
						<Label name="Shipowner's tax number(voluntary)" bold />
						<Controller
							name="shipOwnerTaxNumber"
							control={control}
							defaultValue={B1ShipDetailsData?.shipOwnerTaxNumber ?? ""}
							render={({ field }) => (
								<>
									<Input
										id="shipowner_tax_number"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("shipOwnerTaxNumber")}
										maxLength={255}
										className="form-input"
										required
									/>
									<div className="error-msg-b1ship">{errors?.shipOwnerTaxNumber?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
				</MDBRow>

				<MDBRow className="align-items-center mb-2">
					<MDBCol md="4">
						<Label name="Contact person for the shipowner (voluntary)" bold />
						<Controller
							name="shipOwnerContactPerson"
							control={control}
							defaultValue={B1ShipDetailsData?.shipOwnerContactPerson ?? ""}
							render={({ field }) => (
								<>
									<Input
										id="contact_person"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("shipOwnerContactPerson")}
										maxLength={255}
										className="form-input"
										required
									/>
									<div className="error-msg-b1ship">{errors?.shipOwnerContactPerson?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
					<MDBCol md="4">
						<Label name="Address" bold required />
						<Controller
							name="businessAddress"
							control={control}
							rules={{
								required: "Field is required",
							}}
							defaultValue={B1ShipDetailsData?.businessAddress ?? ""}
							render={({ field }) => (
								<>
									<Input
										id="business_address"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("businessAddress")}
										maxLength={255}
										className="form-input"
										required
									/>
									<div className="error-msg-b1ship">{errors?.businessAddress?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
					<MDBCol md="4">
						<Label name="City (voluntary)" bold />
						<Controller
							name="city"
							control={control}
							defaultValue={B1ShipDetailsData?.city ?? ""}
							render={({ field }) => (
								<>
									<Input
										id="city"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("city")}
										maxLength={255}
										className="form-input"
										required
									/>
									<div className="error-msg-b1ship">{errors?.city?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
				</MDBRow>

				<MDBRow className="align-items-center mb-2">
					<MDBCol md="4">
						<Label name="State/Province/Region (voluntary)" bold />
						<Controller
							name="stateProvinceRegion"
							control={control}
							defaultValue={B1ShipDetailsData?.stateProvinceRegion ?? ""}
							render={({ field }) => (
								<>
									<Input
										id="state"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("stateProvinceRegion")}
										maxLength={255}
										className="form-input"
										required
									/>
									<div className="error-msg-b1ship">{errors?.stateProvinceRegion?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>

					<MDBCol md="4">
						<Label name="Telephone number (voluntary)" bold />
						<Controller
							name="businessTelephone"
							control={control}
							defaultValue={B1ShipDetailsData?.businessTelephone ?? ""}
							rules={{
								validate: (value) => {
									if (!value) return true;
									return /^\+?[0-9]{7,15}$/.test(value) ? true : "Please enter a valid phone number.";
								},
							}}
							render={({ field }) => (
								<>
									<Input
										id="telephone_number"
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("businessTelephone")}
										maxLength={16}
										className="form-input"
									/>
									<div className="error-msg-b1ship">{errors?.businessTelephone?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>

					<MDBCol md="4">
						<Label name="Email address" bold />
						<Controller
							name="businessEmail"
							control={control}
							defaultValue={B1ShipDetailsData?.businessEmail ?? ""}
							rules={{
								validate: (value) => {
									if (!value) return true;
									return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value) ? true : "Please enter a valid email address.";
								},
							}}
							render={({ field }) => (
								<>
									<Input
										id="email_address"
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("businessEmail")}
										maxLength={254}
										className="form-input"
									/>
									<div className="error-msg-b1ship">{errors?.businessEmail?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
				</MDBRow>

				<MDBRow className="align-items-center mb-2">
					<MDBCol md="4">
						<Label name="IMO unique company and registered owner identification number of the registered owner" bold />
						<Controller
							name="registeredOwnerIMO"
							control={control}
							defaultValue={B1ShipDetailsData?.registeredOwnerIMO ?? ""}
							render={({ field }) => (
								<Input
									autoFocus={false}
									id="imo_unique_companu_number"
									className="form-input"
									value={field.value}
									onChange={field.onChange}
									onBlur={() => handleBlur("registeredOwnerIMO")}
								/>
							)}
						/>
					</MDBCol>

					<MDBCol md="4">
						<Label name="Deadweight (in metric tonnes)" bold />

						<Controller
							name="deadweight"
							control={control}
							rules={{
								validate: (value) => /^\d{1,15}(\.\d{0,2})?$/.test(value) || "Please enter a value with size betwen 0 and 15.",
							}}
							defaultValue={B1ShipDetailsData?.deadweight ?? ""}
							render={({ field }) => (
								<>
									<Input
										id="dead_weight"
										type="number"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										onBlur={() => {
											const numericValue = parseFloat(field.value);
											if (!isNaN(numericValue)) {
												field.onChange(numericValue.toFixed(2));
											}
											handleBlur("deadweight");
										}}
										className="form-input"
										maxLength={18}
										required
									/>
									<div className="error-msg-b1ship">{errors?.deadweight?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
					<MDBCol md="4">
						<Label name="Gross Tonnage" bold />
						<Controller
							name="grossTonnage"
							control={control}
							rules={{
								validate: (value) => !value || String(value)?.length <= 9 || "Please enter a number with max 9 digits",
							}}
							defaultValue={B1ShipDetailsData?.grossTonnage ?? ""}
							render={({ field }) => (
								<>
									<Input
										id="gross_tonnage"
										type="number"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										onInput={(e) => {
											e.target.value = e.target.value.replace(/\D/g, "");
											if (e.target.value.length > 9) {
												e.target.value = e.target.value.slice(0, 9);
											}
										}}
										onBlur={() => handleBlur("grossTonnage")}
										className="form-input"
										required
									/>
									<div className="error-msg-b1ship">{errors?.grossTonnage?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
				</MDBRow>
				<MDBRow className="align-items-center mb-2">
					<MDBCol md="4">
						<Label name="Country of registration of the registered owner" bold required />
						<Controller
							name="registeredOwnerCountry"
							control={control}
							rules={{
								required: "Field is required",
							}}
							defaultValue={B1ShipDetailsData?.registeredOwnerCountry ?? ""}
							render={({ field }) => (
								<>
									<Autocomplete
										id="registered_owner_country"
										options={dropdownOptions.countryOwnerOptions}
										getOptionLabel={(option) => option.label}
										isOptionEqualToValue={(option, value) => option.value === value}
										value={dropdownOptions.countryOwnerOptions.find((opt) => opt.value === field.value) || null}
										onChange={(_, selected) => field.onChange(selected?.value || "")}
										onBlur={() => handleBlur("registeredOwnerCountry")}
										renderInput={(params) => (
											<TextField
												{...params}
												fullWidth
												variant="outlined"
												size="small"
												InputProps={{
													...params.InputProps,
													className: "autocomplete-input",
												}}
											/>
										)}
									/>
									<div className="error-msg-b1ship">{errors?.registeredOwnerCountry?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>

					<MDBCol md="4">
						<Label name="Classification Society (voluntary)" bold />
						<Controller
							name="classificationSociety"
							control={control}
							defaultValue={B1ShipDetailsData?.classificationSociety ?? monitoringData?.classificationSociety ?? ""}
							render={({ field }) => (
								<>
									<Autocomplete
										id="classification_society"
										options={dropdownOptions.classificationSocietyOptions}
										getOptionLabel={safeGetOptionLabel}
										isOptionEqualToValue={(o, v) => o?.value === v?.value}
										value={dropdownOptions.classificationSocietyOptions.find((opt) => opt.value === field.value) ?? null}
										onChange={(_, selected) => field.onChange(selected?.value ?? "")}
										onBlur={() => {
											field.onBlur();
											handleBlur("classificationSociety");
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												fullWidth
												variant="outlined"
												size="small"
												InputProps={{
													...params.InputProps,
													className: "autocomplete-input",
												}}
											/>
										)}
									/>
									<div className="error-msg-b1ship">{errors?.classificationSociety?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>

					<MDBCol md="4">
						<Label name="Flag" bold required />
						<Controller
							name="flag"
							control={control}
							rules={{
								required: "Field is required",
							}}
							defaultValue={dropdownOptions.countryFlagOptions.find((opt) => opt.value === B1ShipDetailsData?.flag) || null}
							render={({ field }) => {
								const selectedOption = dropdownOptions.countryFlagOptions.find((opt) => opt.value === field.value) || null;
								return (
									<>
										<Autocomplete
											id="flag"
											options={dropdownOptions.countryFlagOptions}
											getOptionLabel={(option) => option.label || ""}
											isOptionEqualToValue={(option, value) => option.value === value?.value}
											value={selectedOption}
											onChange={(_, selected) => field.onChange(selected?.value || "")}
											onBlur={() => handleBlur(field.name)}
											renderInput={(params) => (
												<TextField
													{...params}
													placeholder="Search Country"
													fullWidth
													variant="outlined"
													size="small"
													InputProps={{
														...params.InputProps,
														className: "autocomplete-input",
													}}
												/>
											)}
										/>
										<div className="error-msg-b1ship">{errors?.classificationSociety?.message || <>&nbsp;</>}</div>
									</>
								);
							}}
						/>
					</MDBCol>
				</MDBRow>
				<MDBRow className="align-items-center mb-2">
					<MDBCol md={isSubTypeRequired ? "2" : "4"}>
						<Label name="Type of the ship" required bold />
						<Controller
							name="shipType"
							control={control}
							rules={{
								required: "Field is required",
								validate: (value) => {
									const isValidOption = dropdownOptions.shipTypes?.some((option) => option.value === value);
									return isValidOption || "Field is required";
								},
							}}
							render={({ field }) => (
								<AbsDropDown
									id="type_of_ship"
									options={dropdownOptions.shipTypes}
									selected={field.value}
									className="compact-dropdown"
									handleChange={(selected) => {
										if (!selected) {
											field.onChange("");
											setIsSubTypeRequired(false);
											setSubTypeOptions([]);
											setValue("shipSubType", "", { shouldValidate: false });
											return;
										}

										field.onChange(selected.value);

										const selectedShipTypeObj = dropdownOptions.shipTypes.find((type) => type.value === selected.value);

										if (selectedShipTypeObj) {
											const shipTypeLabel = selectedShipTypeObj.label;
											const isSubTypeRequired = ["Passenger ship", "Other ship types"].includes(shipTypeLabel);
											setIsSubTypeRequired(isSubTypeRequired);
											setMrvVesselTypeMstId(selectedShipTypeObj.mrvVesselTypeMstId);

											if (isSubTypeRequired) {
												const selectedVal = B1ShipDropdownValues.shipTypes.find((type) => type.name === shipTypeLabel);

												if (selectedVal?.subtypes?.length) {
													const mappedSubtypes = selectedVal.subtypes.map((sub) => ({
														label: sub.name,
														value: sub.code,
													}));
													setSubTypeOptions(mappedSubtypes);
												}
											} else {
												setValue("shipSubType", "", { shouldValidate: false });
												setSubTypeOptions([]);
											}
										}
									}}
									placeholder="Select Type of the ship"
								/>
							)}
						/>
					</MDBCol>

					{isSubTypeRequired && (
						<MDBCol md="2">
							<Label name="Sub Ship Type" bold />
							<Controller
								name="shipSubType"
								control={control}
								rules={{
									required: isSubTypeRequired ? "Field is required" : false,
								}}
								render={({ field }) => (
									<AbsDropDown
										options={subTypeOptions}
										selected={field.value}
										className="compact-dropdown"
										handleChange={(selected) => field.onChange(selected?.value || "")}
										placeholder="Select Sub Ship Type"
										isClearable={!isSubTypeRequired}
									/>
								)}
							/>
						</MDBCol>
					)}

					<MDBCol md={techEffMd}>
						<Label name="Technical Efficiency (voluntary)" bold />
						<Controller
							name="technicalEfficiency"
							control={control}
							defaultValue={B1ShipDetailsData?.technicalEfficiency ?? monitoringData?.technicalEfficiency ?? matchedOption?.value ?? ""}
							render={({ field }) => (
								<>
									<AbsDropDown
										options={dropdownOptions.technicalEfficiencyOptions}
										selected={field.value}
										handleChange={(selected) => {
											field.onChange(selected.value);
											setIsEfficiencyValueRequired(!["na", "not applicable"].includes(selected.value?.toLowerCase()));
										}}
										placeholder="Select Technical Efficiency"
										onBlur={() => handleBlur(field.name)}
										isClearable
									/>
									<div className="error-msg-technicalEfficiency">{"" || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
					{isEfficiencyValueRequired && (
						<MDBCol md="2">
							<Label name="Technical Efficiency Value" bold />
							<Controller
								name="technicalEfficiencyValue"
								control={control}
								defaultValue={B1ShipDetailsData?.technicalEfficiencyValue ?? monitoringData?.technicalEfficiencyValue ?? ""}
								render={({ field }) => (
									<>
										<Input {...field} placeholder="Technical Efficiency Value" autoFocus={false} className="compact-input" />
										<div className="error-msg-b1ship">{"" || <>&nbsp;</>}</div>
									</>
								)}
							/>
						</MDBCol>
					)}

					<MDBCol md={iceClassMd}>
						<Label name="Ice Class" required bold />
						<Controller
							name="isIceClassShip"
							control={control}
							defaultValue={!!B1ShipDetailsData?.IceClassMst?.IceClassMstId || !!B1ShipDetailsData?.iceClass}
							rules={{
								validate: (value) => (value !== null && value !== undefined) || "Field is required",
							}}
							render={({ field }) => (
								<>
									<RadioGroup
										row
										value={String(field.value)}
										onChange={(e) => {
											const val = e.target.value === "true";
											field.onChange(val);
											if (!val) {
												setValue("iceClass", null);
											}
										}}
									>
										<FormControlLabel
											value="true"
											control={
												<Radio
													sx={{
														color: "#032A4F",
														"&.Mui-checked": { color: "#032A4F" },
													}}
												/>
											}
											label="Yes"
										/>
										<FormControlLabel
											value="false"
											control={
												<Radio
													sx={{
														color: "#032A4F",
														"&.Mui-checked": { color: "#032A4F" },
													}}
												/>
											}
											label="No"
										/>
									</RadioGroup>
									<div className="error-msg-b1ship">{errors?.isIceClassShip?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
					{isIceClassYes === true && (
						<MDBCol md="2">
							<Label name="Select Ice Class" required bold />
							<Controller
								name="iceClass"
								control={control}
								defaultValue={B1ShipDetailsData?.iceClass ?? ""}
								rules={{
									validate: (value) => {
										if (isIceClassYes) {
											if (!value) return "Field is required";
											const isValidOption = dropdownOptions?.iceClassOptions?.some((opt) => opt.value === value);
											return isValidOption || "Field is required";
										}
										return true;
									},
								}}
								render={({ field }) => (
									<>
										<AbsDropDown
											options={dropdownOptions?.iceClassOptions}
											selected={field?.value}
											handleChange={(selected) => field.onChange(selected?.value)}
											placeholder="Select Ice Class"
											isClearable
											disabled={!isIceClassYes}
										/>
										<div className="error-msg-b1ship">{errors?.iceClass?.message || <>&nbsp;</>}</div>
									</>
								)}
							/>
						</MDBCol>
					)}
				</MDBRow>
				<MDBRow className="align-items-start mb-2">
					<MDBCol md="4">
						<Label name="Voluntary open description field for additional information about the characteristics of the ship" bold />
						<Controller
							name="additionalInformation"
							control={control}
							render={({ field }) => (
								<TextArea
									id="additional_information"
									className="textarea-resize"
									value={field?.value || ""}
									onChange={field.onChange}
									maxLength={1000}
									rows={1}
								/>
							)}
						/>
					</MDBCol>
					<MDBCol md="4">
						<Label name="Principle place of business" bold />
						<Controller
							name="businessPrinciplePlace"
							control={control}
							render={({ field }) => (
								<TextArea
									id="principle_business"
									className="textarea-resize"
									value={field?.value || ""}
									onChange={field.onChange}
									maxLength={512}
									rows={1}
								/>
							)}
						/>
					</MDBCol>
				</MDBRow>
				<div className="form-row" style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}>
					<Button
						disabled={!isDirty}
						onClick={async () => {
							const isValid = await trigger([...REQUIRED_FIELDS_FOR_B1, "businessTelephone", "businessEmail"]);
							if (!isValid) return;

							manualSaveTriggered.current = true;
							await onSubmit(getValues(), false);
						}}
					>
						Save
					</Button>
				</div>
			</div>
		</>
	);
});

export default B1ShipDetails;
